import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  output,
  viewChild,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import {
  FileUploadControl,
  FileUploadValidators,
  FileUploadModule,
} from '@iplab/ngx-file-upload';
import {
  TranslocoService,
  TranslocoPipe,
  translateSignal,
} from '@jsverse/transloco';
import { InterfaceLanguage } from 'src/app/shared/constants/languages';
import { OnboardingTemplate } from 'src/app/shared/Models/onboarding_template';
import {
  FileService,
  FileUploadApi,
} from 'src/app/shared/Services/files/files.service';
import {
  fetchTasks,
  handleHttpError,
  showSnackbarMessage,
} from 'src/app/shared/ngrx/shared.actions';
import { State } from 'src/app/reducers';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { Menu } from 'src/app/shared/Models/menu';
import { setUserOnboardingTemplateUsed } from 'src/app/dashboard/ngrx/dashboard.actions';
import { afterMenuCreated } from 'src/app/shared/ngrx/menus';
import { SafePipe } from '../../Pipes/safe.pipe';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ImageZoomDirective } from './image-zoom/image-zoom.directive';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'template-item',
  templateUrl: './template-item.component.html',
  styleUrls: [`./template-item.component.scss`],
  imports: [
    MatCardModule,
    ImageZoomDirective,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    FileUploadModule,
    MatDatepickerModule,
    TranslocoPipe,
    SafePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateItemComponent implements OnChanges {
  private translate = inject(TranslocoService);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private ngrxStore = inject<Store<State>>(Store);
  private fileService = inject(FileService);
  private destroyRef = inject(DestroyRef);

  readonly currentLocation = input<number>(undefined);
  readonly editPermissionField = input<string>(undefined);
  readonly latest = input<boolean>(undefined);
  readonly data = input<OnboardingTemplate>(undefined);
  readonly disabled = input(false);
  readonly change = input(false);

  readonly choose = output<Date>();
  readonly changeTemp = output();
  readonly showError = output();
  readonly deleteTemplate = output<number>();
  readonly openNameDialog = output<OnboardingTemplate>();

  readonly picker = viewChild<MatDatepicker<Date>>('picker');
  readonly menuTrigger = viewChild<MatMenuTrigger>('menuTrigger');

  readonly errorMessage = translateSignal('shared.errors.unsupported-file');
  downloadLoading = false;
  public fileUploadControls: FileUploadControl[] = [];
  readonly lang = computed(
    () => this.translate.activeLang() as InterfaceLanguage,
  );

  ngOnChanges(changes: SimpleChanges): void {
    const data = this.data();
    if ('data' in changes && data) {
      this.fileUploadControls = data.data_import.map((provider) => {
        const control = new FileUploadControl(
          {
            listVisible: false,
            discardInvalid: true,
            multiple: false,
          },
          [
            FileUploadValidators.accept([
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/vnd.ms-excel',
            ]),
          ],
        );
        control.valueChanges
          .pipe(
            filter((files) => !!files?.length),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(() => {
            control.valid
              ? this.dispatchFile(control, provider)
              : this.dispatchFileError(control);
          });
        return control;
      });
    }
  }

  editAction(event: Event) {
    event.stopPropagation();
    this.openNameDialog.emit(this.data());
  }

  chooseTemplate() {
    if (!this.latest()) {
      this.choose.emit(this.dateAdapter.today());
      return undefined;
    }
    if (this.data()?.ask_date) {
      this.picker().open();
    } else {
      this.triggerTemplate(this.dateAdapter.today());
    }
  }

  preventDefault(event: Event) {
    event.stopPropagation();
  }

  triggerTemplate(date?) {
    this.picker().close();
    setTimeout(() => {
      this.change()
        ? this.changeTemp.emit()
        : this.choose.emit(date ? date.value : this.dateAdapter.today());
    }, 10);
  }

  dispatchFile(control: FileUploadControl, provider: string): void {
    const data = {
      provider,
      onboarding_template: this.data().id,
      archive: true,
    };
    const api = FileUploadApi.menu;
    const params = {};
    const currentLocation = this.currentLocation();
    if (currentLocation) {
      params['current_location'] = currentLocation;
    }
    this.fileService
      .uploadForm<Menu>(data, control.value, 'attachment', api, params)
      .subscribe({
        next: (menu) => {
          control.clear();
          this.ngrxStore.dispatch(
            setUserOnboardingTemplateUsed({ id: this.data().id, menu }),
          );
          this.ngrxStore.dispatch(afterMenuCreated({ menu }));
          this.ngrxStore.dispatch(fetchTasks());
          this.menuTrigger()?.closeMenu();
        },
        error: (error) => {
          this.ngrxStore.dispatch(handleHttpError({ error }));
          control.clear();
        },
      });
  }

  dispatchFileError(control: FileUploadControl): void {
    control.clear();
    this.ngrxStore.dispatch(
      showSnackbarMessage({ message: this.errorMessage() }),
    );
  }

  downloadTemplate(provider: string) {
    this.downloadLoading = true;
    const onFulfilled = () => (this.downloadLoading = false);
    this.fileService.downloadFile(
      this.fileService.menuImportApi,
      {},
      { provider },
      onFulfilled,
      true,
    );
  }
}
